<template>
<div class="tnm-training-application-query-list">
    <v-card>
      <!-- 頁面標題列（共用元件，融入卡片頂部） -->
      <ListPageHeader
        embedded
        title="培訓隊名單申請清單"
        subtitle="查詢與管理本協會培訓隊名單申請"
        icon="tabler-users-group"
        :count="tca15Vos.length"
      >
        <template #actions>
          <VBtn color="white" variant="flat" class="text-primary font-weight-bold" to="/tca/tnm/TrainingTeamApplication">
            <VIcon icon="tabler-plus" class="mr-1" />建立
          </VBtn>
        </template>
      </ListPageHeader>

      <v-card-item>
        <div class="d-flex align-center mb-1">
          <VIcon icon="tabler-filter" size="20" class="text-primary mr-2" />
          <span class="text-subtitle-1 font-weight-medium">篩選條件</span>
        </div>
        <v-row class="pt-2 pb-5">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="viewParams.filter.applyNo"
              label="申請單號"
              clearable
              prepend-inner-icon="tabler-search"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="viewParams.filter.projectId"
              :items="viewParams.projectSelect"
              item-title="1"
              item-value="0"
              label="所屬計畫項目"
              clearable
              prepend-inner-icon="tabler-search"
              no-data-text="尚無資料"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="viewParams.filter.status"
              :items="viewParams.statusSelect"
              item-title="1"
              item-value="0"
              label="狀態"
              clearable
            ></v-select>
          </v-col>
        </v-row>
      </v-card-item>

      <InteractiveDataTable
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        :items-length="tca15Vos.length"
        :items="pagedItems"
        :headers="headers"
        :loading="loading"
        :row-clickable="false"
        :row-props="rowProps"
        empty-text="尚無培訓隊名單申請"
        empty-subtitle="可調整上方篩選條件後再查詢，或點右上角「建立」新增"
        empty-icon="tabler-users-group"
        :items-per-page-options="[
          {value: 10, title: '10'},
          {value: 25, title: '25'},
          {value: 50, title: '50'},
          {value: 100, title: '100'},
        ]"
      >
        <template v-slot:item.applyNo="{ item }">
          <div class="d-inline-flex align-center flex-nowrap">
            <v-btn
              @click.stop="goto(item)"
              color="primary"
              variant="text"
              class="pa-0 idt-link font-weight-bold"
            >
              {{ item.applyNo }}
            </v-btn>
            <v-tooltip location="top" text="複製單號">
              <template #activator="{ props: tipProps }">
                <v-btn
                  v-bind="tipProps"
                  variant="text"
                  icon
                  size="x-small"
                  density="compact"
                  class="ms-1"
                  @click.stop="notifyCopyDocNo(item.applyNo)"
                >
                  <VIcon icon="tabler-copy" size="18" />
                </v-btn>
              </template>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:item.projectId="{ item }">
          <span class="text-high-emphasis">{{ viewParams.projectTitleMap[item.projectId] }}</span>
        </template>

        <template v-slot:item.applyTime="{ item }">
          <span v-if="item.applyTime" class="d-inline-flex align-center text-medium-emphasis text-no-wrap">
            <VIcon icon="tabler-calendar-event" size="16" class="mr-1" />
            {{ convertToROCDate(item.applyTime,'-') }}
          </span>
          <span v-else class="text-disabled">—</span>
        </template>

        <template v-slot:item.stage="{ item }">
          {{ stageToText(item.stage) }}
        </template>

        <template v-slot:item.status="{ item }">
          <Status
            :status="item.status"
            :is-red="['Rejected', 'Revoke', 'Revoked', '駁回', '收回', '撤回']"
            :is-green="['Approved','Submitted','Resubmitted','Passed']"
            :is-warning="['Draft','ReturnedForRevision']"
            :shared-table-id="tabId"
            btn-class="x-large"
            font-class="18px"
            :status-config="getStatusData"
          />
        </template>
      </InteractiveDataTable>
    </v-card>
</div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import Status from './StatusComponenet.vue'
  import { useRouter } from "vue-router"
  import { useLastViewedRow } from '@/composables/useLastViewedRow'
  import { copyPlainText } from '@/utils/deUtil'
  const router = useRouter()
  definePage({
    meta: {
      action: 'manage',
      subject: 'TRAININGAPPLICATIONQUERYLIST',
    },
  })


  const viewParams = reactive({
    projectSelect: [] ,
    statusSelect: [],
    filter: {
      applyNo: '',
      projectId: '',
      status: ''
    },
    projectTitleMap: {}
  });


  const headers = ref([
    { title: '申請單號', key: 'applyNo', sortable: false, icon: 'tabler-file-text' },
    { title: '計畫項目', key: 'projectId', sortable: false, icon: 'tabler-folder' },
    { title: '申請日期', key: 'applyTime', sortable: false, icon: 'tabler-calendar-event' },
    { title: '階段', key: 'stage', sortable: false, icon: 'tabler-stairs' },
    { title: '狀態', key: 'status', sortable: false, icon: 'tabler-flag' }
  ]);

  const projectSelect = ref([]);
  const statusSelect = ref([]);

  const getSelectList = async () => {
    await $api('/tca/trainingteamquerylist/get/select', {
      method: 'GET',
    }).then((resp) => {
      Object.assign(viewParams.projectSelect, resp.data.projectSelect); 
      viewParams.projectSelect.forEach(select => {
       viewParams.projectTitleMap[select[0]] = select[1];
     });

      Object.assign(viewParams.statusSelect, resp.data.statusSelect); 
    }).catch((error) => {
      console.error('取得下拉選單失敗:', error);
    });
  };

  const tca15Vos = reactive([]);
  const page = ref(1)
  const itemsPerPage = ref(100)
  const loading = ref(false)
  const { setLastViewed: setLastClickedId, rowProps } = useLastViewedRow('tnm_training_application_query_list_last_clicked', 'applyNo')

  // client 端分頁切片（資料一次抓回，前端分頁）
  const pagedItems = computed(() => {
    if (itemsPerPage.value === -1) return tca15Vos
    const start = (page.value - 1) * itemsPerPage.value

    return tca15Vos.slice(start, start + itemsPerPage.value)
  })

  const fetchData = async () => {
    tca15Vos.length = 0;
    const projectId = viewParams.filter.projectId ? viewParams.filter.projectId : null;
    const status = viewParams.filter.status ? viewParams.filter.status : null;
    const applyNo = viewParams.filter.applyNo ? viewParams.filter.applyNo : null;
    page.value = 1
    loading.value = true
    await $api('/tca/trainingteamquerylist/search', {
      method: 'POST',
      body: {
        projectId : projectId,
        status : status,
        applyNo : applyNo
      }
    }).then((response) => {
      Object.assign(tca15Vos, response.data); 
    }).catch((error) => {
      console.error('查詢失敗:', error);
    }).finally(() => {
      loading.value = false
    });
  };

    // 下拉 API

  const createNew = () => {
    // 實現新增邏輯
  };

  watch(viewParams.filter, () => {
    fetchData();
  })

  onMounted(() => {
    getSelectList();
    fetchData();
  })

const stageToText = (stage) => {
  // 轉換 第一階段：1，第二階段第一期：2.1 ，以此類推
  let dispStage = '';
  const regex = /(\d+)\.?(\d?)/;
  const matches = stage.match(regex);
  if(matches) {
    let cNum = numberToChinese(matches[1]);
    dispStage = '第' + cNum + '階段';
    if(matches[2]) {
      cNum = numberToChinese(matches[2]);
      dispStage = dispStage + '第' + cNum +'期';
    }
  } else {
    dispStage = '建議參賽名單';
  }
  return dispStage;
};

function numberToChinese(num) {
  const chineseDigits = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
  let chineseNum = '';
  // 处理整数部分
  const integerPart = Math.floor(num);
  if (integerPart > 0) {
    const digits = integerPart.toString().split('').reverse();
    for (let i = 0; i < digits.length; i++) {
      if (digits[i] !== '0') {
        chineseNum = chineseDigits[digits[i]] +  chineseNum;
      } else {
        // 处理连续的零
        if (chineseNum.length > 0 && chineseNum[chineseNum.length - 1] !== '零') {
          chineseNum += '零';
        }
      }
    }
  }
  return chineseNum;
}

const goto = (item) => {
  setLastClickedId(item.applyNo)
  router.push({ path: '/tca/tnm/TrainingTeamApplication', query: {
       applyNo: item.applyNo, 
  }})
}

const notifyCopyDocNo = async (text: string | null | undefined) => {
  const ok = await copyPlainText(text)
  $swal({
    icon: ok ? 'success' : 'error',
    title: ok ? '已複製到剪貼簿' : '複製失敗',
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: ok ? 1500 : 2000,
  })
}

const tabId = ref('TrainingTeamApplication')
const { data: getStatusData, execute: fetchstatusDatas } = await useApi<any>(createUrl('/q/de/select/select/select/deSelect', {
  query: {
    tabId: tabId.value
  },
}))


</script>

<style scoped>

</style>
